/* ============================================================
   OhCrisp 19-Fruit Sampler — Builder Logic
   Reads config from <script id="sampler-config" type="application/json">
   Adds to Shopify cart via /cart/add.js (multiple line items)
   ============================================================ */

(function() {
  'use strict';

  // Tunable constants (rarely change)
  const MIN_MIX_BAGS = 0;   // mixes are optional (often out of stock); box just needs ≥1 item
  const FREE_SHIPPING_THRESHOLD = 40;
  const CART_REDIRECT = '/cart';   // set to null to fire cart:added event instead
  const CART_ENDPOINT = '/cart/add.js';

  // Pull config injected by the Liquid section
  const configEl = document.getElementById('sampler-config');
  if (!configEl) {
    console.error('[sampler] No #sampler-config found. Builder cannot start.');
    return;
  }
  let CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (err) {
    console.error('[sampler] Invalid config JSON', err);
    return;
  }

  // CONFIG shape:
  // {
  //   prices: { mix: 16.99, single: 4.99 },
  //   mixes: [
  //     { id: "classic", name: "Classic Mix", variant_id: 1234, fruits: [...], ... },
  //     { id: "discovery", ... },
  //   ],
  //   singles: [
  //     { id: "strawberry", name: "Strawberry", variant_id: 5678, cut: "Whole", ... },
  //   ],
  //   presets: [
  //     { id: "bestseller", label: "⭐ Bestseller", mixes: { classic: 1, discovery: 1 }, singles: { strawberry: 1, ... } },
  //   ],
  //   strings: { ... all UI copy ... }
  // }

  const STATE = { mixes: {}, singles: {} };
  CONFIG.mixes.forEach(m => STATE.mixes[m.id] = 0);

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  // -------------- Analytics --------------
  function fire(name, data = {}) {
    try {
      document.dispatchEvent(new CustomEvent('sampler:event', {
        detail: { name, data, state: snapshotState() }
      }));
    } catch (e) {}
  }
  function snapshotState() {
    return {
      mixes: { ...STATE.mixes },
      singles: { ...STATE.singles },
      total: calcTotal(),
      fruitCount: countFruits().size,
    };
  }

  // -------------- Hero count-up --------------
  function animateCount(el, target, duration = 900) {
    const start = performance.now();
    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      el.textContent = Math.round(target * eased);
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = target;
    }
    requestAnimationFrame(tick);
  }
  window.addEventListener('load', () => {
    $$('[data-anim-num]').forEach((el, i) => {
      setTimeout(() => animateCount(el, parseInt(el.dataset.animNum, 10)), 200 + i * 200);
    });
    fire('view_lp');
  });

  // -------------- Magnetic CTA --------------
  if (!window.matchMedia('(hover: none)').matches) {
    $$('[data-magnetic]').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.25}px)`;
      });
      btn.addEventListener('mouseleave', () => btn.style.transform = 'translate(0, 0)');
    });

    // Mix card tilt
    $$('[data-tilt]').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(900px) rotateX(${y * -3}deg) rotateY(${x * 3}deg) translateY(-2px)`;
      });
      card.addEventListener('mouseleave', () => card.style.transform = '');
    });
  }

  // -------------- Confetti on free shipping --------------
  let confettiFired = false;
  function fireConfetti() {
    if (confettiFired) return;
    confettiFired = true;
    fire('free_shipping_unlocked');
    const container = document.createElement('div');
    container.className = 'confetti-container';
    document.body.appendChild(container);
    const emojis = ['🍓','🍑','🥭','🥥','🫐','🍍','🌸','✨'];
    for (let i = 0; i < 28; i++) {
      const piece = document.createElement('div');
      piece.className = 'confetti-piece';
      piece.textContent = emojis[Math.floor(Math.random() * emojis.length)];
      piece.style.left = (Math.random() * 100) + '%';
      piece.style.top = '-40px';
      piece.style.fontSize = (16 + Math.random() * 16) + 'px';
      piece.style.setProperty('--x-drift', (Math.random() * 200 - 100) + 'px');
      piece.style.setProperty('--rot', (Math.random() * 720 - 360) + 'deg');
      piece.style.animationDelay = (Math.random() * 0.3) + 's';
      container.appendChild(piece);
    }
    setTimeout(() => container.remove(), 2400);
  }

  // -------------- Math helpers --------------
  function totalMixBags() {
    return Object.values(STATE.mixes).reduce((s, n) => s + n, 0);
  }
  function totalSingles() {
    return Object.values(STATE.singles).reduce((s, n) => s + n, 0);
  }
  // Each mix/single carries its own price (pulled from its Shopify product).
  function priceOf(item) {
    return (item && typeof item.price === 'number') ? item.price : 0;
  }
  // Representative single-bag price for the free-shipping nudge estimate.
  function singleBagPrice() {
    const s = CONFIG.singles.find(x => typeof x.price === 'number' && x.price > 0);
    return s ? s.price : 0;
  }
  function calcTotal() {
    let sum = 0;
    Object.entries(STATE.mixes).forEach(([id, qty]) => {
      if (qty > 0) sum += qty * priceOf(CONFIG.mixes.find(x => x.id === id));
    });
    Object.entries(STATE.singles).forEach(([id, qty]) => {
      if (qty > 0) sum += qty * priceOf(CONFIG.singles.find(x => x.id === id));
    });
    return sum;
  }
  function countFruits() {
    // Dedupe by a normalized key (trimmed + lowercased) so the same fruit never
    // shows twice — e.g. " Pineapple" (from "a, Pineapple" CSV spacing) vs
    // "Pineapple", or "Mango" vs "mango". First-seen spelling is what's displayed.
    const byKey = new Map();
    const add = (name) => {
      const display = String(name == null ? '' : name).trim();
      if (!display) return;
      const key = display.toLowerCase();
      if (!byKey.has(key)) byKey.set(key, display);
    };
    Object.entries(STATE.mixes).forEach(([id, qty]) => {
      if (qty > 0) {
        const m = CONFIG.mixes.find(x => x.id === id);
        if (m && m.fruits) m.fruits.forEach(add);
      }
    });
    Object.entries(STATE.singles).forEach(([id, qty]) => {
      if (qty > 0) {
        const s = CONFIG.singles.find(x => x.id === id);
        if (s) add(s.name);
      }
    });
    return new Set(byKey.values());
  }
  // "Inside your box" tile image for a fruit name (lowercased key). A "Fruit photo"
  // block (custom) wins; otherwise the matching single-bag photo (auto) is reused.
  // Null → text tile.
  function fruitImage(name) {
    const key = String(name).toLowerCase().trim();
    const custom = CONFIG.fruit_images_custom || {};
    const auto = CONFIG.fruit_images_auto || {};
    return custom[key] || auto[key] || null;
  }

  // -------------- Mix steppers --------------
  $$('[data-mix-stepper]').forEach(btn => {
    btn.addEventListener('click', () => {
      const mixId = btn.dataset.mixStepper;
      const action = btn.dataset.action;
      if (action === 'plus') {
        STATE.mixes[mixId]++;
        fire('mix_added', { mixId, qty: STATE.mixes[mixId] });
      } else {
        if (STATE.mixes[mixId] > 0) {
          STATE.mixes[mixId]--;
          fire('mix_removed', { mixId, qty: STATE.mixes[mixId] });
        }
      }
      clearActivePreset();
      updateUI();
    });
  });

  // -------------- Single steppers + info --------------
  const singleGrid = $('#singleGrid');
  if (singleGrid) {
    singleGrid.addEventListener('click', (e) => {
      const stepperBtn = e.target.closest('[data-single-stepper]');
      if (stepperBtn) {
        const fruitId = stepperBtn.dataset.singleStepper;
        const action = stepperBtn.dataset.action;
        if (!STATE.singles[fruitId]) STATE.singles[fruitId] = 0;
        if (action === 'plus') {
          STATE.singles[fruitId]++;
          fire('single_added', { fruitId, qty: STATE.singles[fruitId] });
        } else if (STATE.singles[fruitId] > 0) {
          STATE.singles[fruitId]--;
          fire('single_removed', { fruitId, qty: STATE.singles[fruitId] });
        }
        if (STATE.singles[fruitId] === 0) delete STATE.singles[fruitId];
        clearActivePreset();
        updateUI();
        return;
      }
      // "See details" → enlarge the uploaded back-label image (falls back to front).
      const infoBtn = e.target.closest('[data-info]');
      if (infoBtn) {
        const s = CONFIG.singles.find(x => x.id === infoBtn.dataset.info);
        const src = s && (s.photo_back || s.photo_front);
        if (src) {
          openLightbox(src, (s.name || '') + ' label');
        } else {
          const img = infoBtn.closest('.single-card')?.querySelector('.single-photo img');
          if (img) openLightbox(img.currentSrc || img.src, img.alt);
        }
        return;
      }
      // Tapping the front photo → enlarge it.
      const photoBtn = e.target.closest('[data-info-photo]');
      if (photoBtn) {
        const img = photoBtn.querySelector('img');
        if (img) openLightbox(img.currentSrc || img.src, img.alt);
      }
    });
  }

  // -------------- Single-fruit swipe pager dots (mobile) --------------
  (function setupSinglePager() {
    const grid = $('#singleGrid');
    const dotsWrap = $('#singlePagerDots');
    if (!grid || !dotsWrap) return;
    const pages = $$('.single-page', grid);
    if (pages.length <= 1) return;   // 0–1 page: no pager needed
    pages.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('aria-label', 'Go to single-fruit page ' + (i + 1));
      dot.addEventListener('click', () => {
        const left = pages[i].getBoundingClientRect().left - grid.getBoundingClientRect().left + grid.scrollLeft;
        grid.scrollTo({ left, behavior: 'smooth' });
      });
      dotsWrap.appendChild(dot);
    });
    const dotEls = $$('.dot', dotsWrap);
    let raf = null;
    grid.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const gridLeft = grid.getBoundingClientRect().left;
        let best = 0, bestDist = Infinity;
        pages.forEach((p, i) => {
          const dist = Math.abs(p.getBoundingClientRect().left - gridLeft);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        dotEls.forEach((d, i) => d.classList.toggle('active', i === best));
      });
    }, { passive: true });
  })();

  // -------------- Desktop: keep "Your Crunch Box" in view as you scroll --------------
  // Uses translateY rather than position:sticky, which a theme ancestor with
  // overflow can silently break.
  (function setupBoxFollow() {
    const track = $('.builder-layout');
    const col = $('.box-col');
    const box = $('#boxPreview');
    if (!track || !col || !box) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    let ticking = false;
    function apply() {
      ticking = false;
      if (!desktop.matches) { box.style.transform = ''; return; }
      const colTop = col.getBoundingClientRect().top; // box's natural top (col is never transformed)
      const tr = track.getBoundingClientRect();
      const boxH = box.offsetHeight;
      const vh = window.innerHeight;
      if (boxH > vh - 24) return;                     // too tall to center — leave in flow
      const desired = (vh - boxH) / 2;                // vertically centered in viewport
      const target = Math.max(tr.top, Math.min(desired, tr.bottom - boxH));
      box.style.transform = `translateY(${Math.round(target - colTop)}px)`;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (desktop.addEventListener) desktop.addEventListener('change', onScroll);
    apply();
  })();

  // -------------- Mix details toggles --------------
  $$('.mix-details-toggle').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const target = $('#' + btn.dataset.toggle);
      if (!target) return;
      target.classList.toggle('open');
      btn.textContent = target.classList.contains('open')
        ? (CONFIG.strings.hide_details || 'Hide bag details ↑')
        : (CONFIG.strings.see_details  || 'See bag details ↓');
      if (target.classList.contains('open')) {
        fire('bag_details_opened', { mixId: btn.dataset.toggle.replace('-details', '') });
      }
    });
  });

  // -------------- Presets (animated fill) --------------
  async function applyPresetAnimated(preset) {
    // clear
    Object.keys(STATE.mixes).forEach(k => STATE.mixes[k] = 0);
    STATE.singles = {};
    updateUI();
    await new Promise(r => setTimeout(r, 120));
    // mixes (skip any that are sold out so they never enter the box / price)
    for (const [mixId, qty] of Object.entries(preset.mixes || {})) {
      const m = CONFIG.mixes.find(x => x.id === mixId);
      if (!m || m.available === false) continue;
      for (let i = 0; i < qty; i++) {
        STATE.mixes[mixId] = (STATE.mixes[mixId] || 0) + 1;
        updateUI();
        await new Promise(r => setTimeout(r, 160));
      }
    }
    // singles (skip sold out)
    for (const [fruitId, qty] of Object.entries(preset.singles || {})) {
      const s = CONFIG.singles.find(x => x.id === fruitId);
      if (!s || s.available === false) continue;
      for (let i = 0; i < qty; i++) {
        STATE.singles[fruitId] = (STATE.singles[fruitId] || 0) + 1;
        updateUI();
        await new Promise(r => setTimeout(r, 130));
      }
    }
  }

  $$('.preset-chip').forEach(chip => {
    chip.addEventListener('click', async () => {
      const presetId = chip.dataset.preset;
      chip.classList.add('applying');
      setTimeout(() => chip.classList.remove('applying'), 500);
      $$('.preset-chip').forEach(c => c.classList.remove('active'));

      if (presetId === 'reset') {
        Object.keys(STATE.mixes).forEach(k => STATE.mixes[k] = 0);
        STATE.singles = {};
        confettiFired = false;
        updateUI();
        fire('preset_click', { presetId: 'reset' });
        return;
      }

      const preset = CONFIG.presets.find(p => p.id === presetId);
      if (!preset) return;

      chip.classList.add('active');
      const applied = $('#presetApplied');
      if (applied) {
        applied.classList.add('show');
        setTimeout(() => applied.classList.remove('show'), 2400);
      }
      fire('preset_click', { presetId });
      await applyPresetAnimated(preset);
    });
  });

  function clearActivePreset() {
    $$('.preset-chip').forEach(c => c.classList.remove('active'));
  }

  // -------------- Reset --------------
  const resetBtn = $('#resetBtn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      Object.keys(STATE.mixes).forEach(k => STATE.mixes[k] = 0);
      STATE.singles = {};
      clearActivePreset();
      confettiFired = false;
      updateUI();
    });
  }

  // -------------- Skip-singles nudge --------------
  const skipBtn = $('#skipSingle');
  if (skipBtn) {
    skipBtn.addEventListener('click', () => {
      fire('skip_singles_clicked');
      if (totalMixBags() + totalSingles() === 0) {
        // Nothing in the box yet — send them back up to pick something.
        $('.mix-grid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const cartTotal = calcTotal();
      const singlePrice = singleBagPrice();
      if (cartTotal < FREE_SHIPPING_THRESHOLD && singlePrice > 0) {
        const gap = FREE_SHIPPING_THRESHOLD - cartTotal;
        const bagsNeeded = Math.ceil(gap / singlePrice);
        const bagsCost = (bagsNeeded * singlePrice).toFixed(2);
        $('#nudgeGap').innerHTML =
          `Your box is at <strong>$${cartTotal.toFixed(2)}</strong>.<br>` +
          `Adding <strong>${bagsNeeded} single bag${bagsNeeded > 1 ? 's' : ''}</strong> ($${bagsCost}) ` +
          `gets you to <strong>free shipping</strong> AND a fuller box.`;
        showModal('nudgeOverlay');
      } else {
        $('#addCartBtn')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  $('#nudgeAddPopular')?.addEventListener('click', () => {
    // Add the configured "popular" defaults
    const popular = CONFIG.nudge_popular_singles || ['strawberry', 'mango'];
    popular.forEach(id => {
      const s = CONFIG.singles.find(x => x.id === id);
      if (s && s.available !== false) {   // never add a sold-out single
        STATE.singles[id] = (STATE.singles[id] || 0) + 1;
      }
    });
    hideModal('nudgeOverlay');
    clearActivePreset();
    updateUI();
    fire('nudge_accepted');
  });
  $('#nudgeSkipAnyway')?.addEventListener('click', () => {
    hideModal('nudgeOverlay');
    $('#addCartBtn')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    fire('nudge_declined');
  });

  // -------------- Nudge modal --------------
  $('#nudgeClose')?.addEventListener('click', () => hideModal('nudgeOverlay'));
  $('#nudgeOverlay')?.addEventListener('click', (e) => { if (e.target.id === 'nudgeOverlay') hideModal('nudgeOverlay'); });
  function showModal(id) { $('#' + id)?.classList.add('show'); document.body.style.overflow = 'hidden'; }
  function hideModal(id) { $('#' + id)?.classList.remove('show'); document.body.style.overflow = ''; }

  // -------------- Branded notice / confirm modal (replaces browser alert/confirm) --------------
  function showNotice({ icon = '🛒', title = '', msg = '', okText = 'Got it', cancelText = null } = {}) {
    return new Promise(resolve => {
      const ov = $('#noticeOverlay');
      if (!ov) { // fallback if markup missing
        if (cancelText) resolve(window.confirm(`${title}\n\n${String(msg).replace(/<[^>]+>/g, '')}`));
        else { window.alert(`${title}\n\n${String(msg).replace(/<[^>]+>/g, '')}`); resolve(true); }
        return;
      }
      $('#noticeIcon').textContent = icon;
      $('#noticeTitle').textContent = title;
      $('#noticeMsg').innerHTML = msg;
      const okBtn = $('#noticeOk'), cancelBtn = $('#noticeCancel'), closeBtn = $('#noticeClose');
      okBtn.textContent = okText;
      cancelBtn.style.display = cancelText ? '' : 'none';
      if (cancelText) cancelBtn.textContent = cancelText;
      showModal('noticeOverlay');
      const finish = (val) => {
        hideModal('noticeOverlay');
        okBtn.removeEventListener('click', onOk);
        cancelBtn.removeEventListener('click', onCancel);
        closeBtn.removeEventListener('click', onCancel);
        ov.removeEventListener('click', onBackdrop);
        resolve(val);
      };
      const onOk = () => finish(true);
      const onCancel = () => finish(false);
      const onBackdrop = (e) => { if (e.target === ov) finish(false); };
      okBtn.addEventListener('click', onOk);
      cancelBtn.addEventListener('click', onCancel);
      closeBtn.addEventListener('click', onCancel);
      ov.addEventListener('click', onBackdrop);
    });
  }

  // -------------- Image lightbox (bag detail zoom) --------------
  const lightbox = $('#imgLightbox');
  const lightboxImg = $('#imgLightboxImg');
  function openLightbox(src, alt) {
    if (!lightbox || !lightboxImg || !src) return;
    lightboxImg.src = src;
    lightboxImg.alt = alt || '';
    lightbox.classList.add('show');
    document.body.style.overflow = 'hidden';
    fire('bag_image_zoomed', { src });
  }
  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('show');
    document.body.style.overflow = '';
  }
  $$('.mix-detail-grid').forEach(grid => {
    grid.addEventListener('click', (e) => {
      const img = e.target.closest('img');
      if (img) openLightbox(img.currentSrc || img.src, img.alt);
    });
  });
  $('#imgLightboxClose')?.addEventListener('click', closeLightbox);
  lightbox?.addEventListener('click', () => closeLightbox());

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideModal('nudgeOverlay');
      closeLightbox();
    }
  });

  // -------------- Add to cart (Shopify) --------------
  async function addToShopifyCart() {
    // Gather everything the customer selected, with its product config.
    const selected = [];
    Object.entries(STATE.mixes).forEach(([id, qty]) => {
      if (qty > 0) { const m = CONFIG.mixes.find(x => x.id === id); if (m) selected.push({ cfg: m, qty }); }
    });
    Object.entries(STATE.singles).forEach(([id, qty]) => {
      if (qty > 0) { const s = CONFIG.singles.find(x => x.id === id); if (s) selected.push({ cfg: s, qty }); }
    });

    const noProduct = selected.filter(x => !x.cfg.variant_id);
    const soldOut   = selected.filter(x => x.cfg.variant_id && x.cfg.available === false);
    const addable   = selected.filter(x => x.cfg.variant_id && x.cfg.available !== false);

    // Diagnostic: shows the resolved variant_id / available for each selected item.
    console.table(selected.map(x => ({ item: x.cfg.name, qty: x.qty, variant_id: x.cfg.variant_id, available: x.cfg.available })));

    // Merge duplicate variant ids so each variant is ONE line with the summed
    // quantity (Shopify rejects/merges repeated ids — this keeps totals correct
    // and ensures every distinct product is sent).
    const byVariant = new Map();
    addable.forEach(x => byVariant.set(x.cfg.variant_id, (byVariant.get(x.cfg.variant_id) || 0) + x.qty));
    const items = Array.from(byVariant, ([id, quantity]) => ({ id, quantity }));

    if (items.length === 0) {
      if (noProduct.length) {
        await showNotice({ icon: '⚙️', title: 'Not available yet', msg: 'These items don\'t have a product set up yet, so they can\'t be added: <strong>' + noProduct.map(x => x.cfg.name).join(', ') + '</strong>.' });
      } else if (soldOut.length) {
        await showNotice({ icon: '😢', title: 'Sold out', msg: 'These are sold out right now: <strong>' + soldOut.map(x => x.cfg.name).join(', ') + '</strong>. Try some others below!' });
      } else {
        await showNotice({ icon: '🍓', title: 'Your box is empty', msg: 'Add at least one mix or single bag to get started.' });
      }
      return;
    }

    // Tell the customer if we had to drop anything from the order (branded confirm).
    const dropped = [...soldOut.map(x => x.cfg.name + ' (sold out)'), ...noProduct.map(x => x.cfg.name)];
    if (dropped.length) {
      const proceed = await showNotice({
        icon: '😢', title: 'A couple of items are unavailable',
        msg: 'We\'ll skip these and add the rest:<br><strong>' + dropped.join('<br>') + '</strong>',
        okText: 'Add the rest', cancelText: 'Go back'
      });
      if (!proceed) return;
    }

    console.log('[sampler] adding to cart:', items);

    const btn = $('#addCartBtn');
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      const resp = await fetch(CART_ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        console.error('[sampler] cart add rejected:', err);
        throw new Error(err.description || err.message || 'Cart add failed');
      }
      const result = await resp.json();
      fire('add_to_cart', { items, total: calcTotal(), result });

      if (CART_REDIRECT) {
        window.location.href = CART_REDIRECT;
      } else {
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { items, result } }));
        btn.textContent = '✓ Added to cart';
        setTimeout(() => { btn.disabled = false; btn.textContent = originalText; }, 2000);
      }
    } catch (err) {
      console.error('[sampler] cart error', err);
      btn.disabled = false;
      btn.textContent = originalText;
      await showNotice({ icon: '⚠️', title: 'Something went wrong', msg: 'We couldn\'t add your box to the cart just now. Please try again.<br><small style="color:var(--s-ink-mute)">' + (err.message || '') + '</small>' });
    }
  }
  $('#addCartBtn')?.addEventListener('click', addToShopifyCart);
  $('#mobileCta')?.addEventListener('click', () => {
    if (totalMixBags() + totalSingles() > 0) addToShopifyCart();
    else $('#builder')?.scrollIntoView({ behavior: 'smooth' });
  });

  // -------------- UI updates --------------
  let prevFruitCount = 0;
  function updateUI() {
    // Mix qty + states
    CONFIG.mixes.forEach(m => {
      const qty = STATE.mixes[m.id] || 0;
      const soldOut = m.available === false;
      const qtyEl = $(`[data-mix-qty="${m.id}"]`);
      if (qtyEl) qtyEl.textContent = qty;
      const card = $(`[data-mix="${m.id}"]`);
      if (card) {
        card.classList.toggle('has-qty', qty > 0);
        card.classList.toggle('sold-out', soldOut);
      }
      const minus = $(`[data-mix-stepper="${m.id}"][data-action="minus"]`);
      const plus  = $(`[data-mix-stepper="${m.id}"][data-action="plus"]`);
      if (minus) minus.disabled = qty === 0;
      if (plus)  plus.disabled  = soldOut;   // no upper limit, but block when sold out
    });

    const total = totalMixBags();
    // The "full variety unlocked" message needs one of EACH mix type (e.g. Classic
    // AND Discovery for the full 15 fruits), not just any single mix bag.
    const distinctMixes = Object.values(STATE.mixes).filter(q => q > 0).length;
    const needTypes = Math.min(2, CONFIG.mixes.length || 0);
    const fullVariety = needTypes > 0 && distinctMixes >= needTypes;
    const allMixesSoldOut = CONFIG.mixes.length > 0 && CONFIG.mixes.every(m => m.available === false);
    const balance = $('#mixBalance');
    if (balance) {
      balance.classList.remove('warn', 'success');
      const S = CONFIG.strings;
      if (total === 0 && allMixesSoldOut) {
        // No mix can be picked right now — guide them straight to building with singles.
        balance.innerHTML = `<span>🙌</span><span>${S.balance_soldout || '<strong>Mixes are sold out right now.</strong> No problem — skip the mix and build your box with the singles below!'}</span>`;
      } else if (total === 0) {
        balance.innerHTML = `<span>💡</span><span>${S.balance_zero || '<strong>Mixes are optional.</strong> Add one for instant variety, or skip straight to singles below.'}</span>`;
      } else if (!fullVariety) {
        balance.classList.add('warn');
        balance.innerHTML = `<span>👉</span><span>${S.balance_one || '<strong>One mix in.</strong> Add the other mix to unlock the full fruit variety.'}</span>`;
      } else {
        balance.classList.add('success');
        balance.innerHTML = `<span>✨</span><span>${S.balance_two || '<strong>Full variety unlocked!</strong> Add singles below for even more.'}</span>`;
      }
    }

    // Single qty + states
    CONFIG.singles.forEach(s => {
      const card = $(`.single-card[data-fruit="${s.id}"]`);
      if (!card) return;
      const qty = STATE.singles[s.id] || 0;
      const soldOut = s.available === false;
      const qtyEl = card.querySelector(`[data-single-qty="${s.id}"]`);
      if (qtyEl) qtyEl.textContent = qty;
      card.classList.toggle('has-qty', qty > 0);
      card.classList.toggle('sold-out', soldOut);
      const minus = card.querySelector('[data-action="minus"]');
      const plus  = card.querySelector('[data-action="plus"]');
      if (minus) minus.disabled = qty === 0;
      if (plus)  plus.disabled  = soldOut;
    });

    // Fruit count
    const fruitsSet = countFruits();
    const fruitCount = fruitsSet.size;
    const countEl = $('#fruitCount');
    if (countEl) {
      countEl.textContent = fruitCount;
      if (fruitCount !== prevFruitCount) {
        countEl.classList.remove('changing');
        void countEl.offsetWidth;
        countEl.classList.add('changing');
        prevFruitCount = fruitCount;
      }
    }

    // Hero subline
    const heroSub = $('#heroSub');
    if (heroSub) {
      const hasContent = total > 0 || Object.keys(STATE.singles).length > 0;
      if (hasContent) {
        heroSub.innerHTML = (CONFIG.strings.hero_sub_active || 'You\'re building a <strong style="color:var(--s-pink-deep)">{count}-fruit</strong> box. Tweak it below — or scroll to checkout.')
          .replace('{count}', fruitCount);
      } else {
        heroSub.textContent = CONFIG.strings.hero_sub || '';
      }
    }

    // Box items
    const items = $('#boxItems');
    if (items) {
      let html = '';
      CONFIG.mixes.forEach(m => {
        const qty = STATE.mixes[m.id] || 0;
        if (qty > 0) html += `<div class="box-item"><span class="check">✓</span> ${m.name} <span class="box-item-qty">× ${qty}</span></div>`;
      });
      Object.entries(STATE.singles).forEach(([fid, qty]) => {
        const s = CONFIG.singles.find(x => x.id === fid);
        if (s) html += `<div class="box-item"><span class="check">✓</span> ${s.name} <span class="box-item-qty">× ${qty}</span></div>`;
      });
      const hasSingles = Object.keys(STATE.singles).length > 0;
      if (total === 0 && !hasSingles) {
        html += `<div class="box-item empty"><span class="check">○</span> Add mixes or singles to start</div>`;
      } else if (total > 0 && !hasSingles) {
        html += `<div class="box-item empty"><span class="check">○</span> Add singles (optional)</div>`;
      }
      if (CONFIG.show_free_gift !== false) {
        html += `<div class="box-item"><span class="check gift">🎁</span> <span class="gift">FREE</span> ${CONFIG.strings.free_gift || 'whole banana'}</div>`;
      }
      items.innerHTML = html;
    }

    // Inside-mix grid
    const insideMix = $('#insideMix');
    const insideGrid = $('#insideMixGrid');
    const insideTitle = $('#insideMixTitle');
    if (insideMix && insideGrid && insideTitle) {
      if (fruitsSet.size > 0) {
        insideMix.classList.add('visible');
        insideTitle.textContent = `Inside your box · ${fruitsSet.size} fruits`;
        insideGrid.innerHTML = Array.from(fruitsSet).map((f, i) => {
          const img = fruitImage(f);
          if (img) {
            return `<div class="tile has-img" style="animation-delay:${i*30}ms"><img src="${img}" alt="${f}" loading="lazy"></div>`;
          }
          const short = f.length > 8 ? f.slice(0, 7) + '…' : f;
          return `<div class="tile" style="animation-delay:${i*30}ms">${short}</div>`;
        }).join('');
      } else {
        insideMix.classList.remove('visible');
      }
    }

    // Pricing
    const cartTotal = calcTotal();
    const priceEl = $('#priceTotal');
    const mobilePriceEl = $('#mobilePrice');
    if (priceEl) priceEl.textContent = cartTotal.toFixed(2);
    if (mobilePriceEl) mobilePriceEl.textContent = cartTotal.toFixed(2);

    const strike = $('#priceStrike');
    if (strike) {
      if (cartTotal > 0) {
        strike.textContent = `$${(cartTotal * 1.15).toFixed(2)}`;
        strike.style.display = 'inline';
      } else {
        strike.style.display = 'none';
      }
    }

    // Shipping bar
    const shipPct = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
    const fillEl = $('#shippingFill');
    if (fillEl) fillEl.style.width = shipPct + '%';
    const labelEl = $('#shippingLabel');
    const amountEl = $('#shippingAmount');
    if (labelEl && amountEl) {
      if (cartTotal >= FREE_SHIPPING_THRESHOLD) {
        labelEl.textContent = '🎉 Free shipping unlocked!';
        amountEl.textContent = '$' + cartTotal.toFixed(2);
        if (fillEl) fillEl.classList.add('full');
        fireConfetti();
      } else {
        labelEl.textContent = `$${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} from free shipping`;
        amountEl.textContent = `$${cartTotal.toFixed(2)} / $${FREE_SHIPPING_THRESHOLD}`;
        if (fillEl) fillEl.classList.remove('full');
        confettiFired = false;
      }
    }

    // CTA states — mixes are optional, so the box just needs at least one item.
    const btn = $('#addCartBtn');
    const mobileBtn = $('#mobileCta');
    const mobileStatus = $('#mobileStatus');
    const hasItems = total + totalSingles() > 0;
    if (btn && mobileBtn && mobileStatus) {
      if (hasItems) {
        btn.disabled = false;
        btn.textContent = `Add to cart — $${cartTotal.toFixed(2)}`;
        mobileBtn.textContent = 'Add to cart';
        mobileStatus.textContent = `${fruitCount} fruits in box`;
      } else {
        btn.disabled = true;
        btn.textContent = CONFIG.strings.cta_initial || 'Add mixes or singles to start';
        mobileBtn.textContent = 'Build →';
        mobileStatus.textContent = 'Build your box';
      }
    }
  }

  updateUI();
})();